import axios from 'axios';
import { format } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import some from 'lodash/some';
import {
  FetchDDLResponse,
  FetchLatestSplitResponse,
  Link,
} from 'types/api.types';
import {
  ContributionPatchValidationSchemaResponse,
  ContributionValidationSchemaResponse,
  CreateContributionResponse,
  FetchContributionResponse,
  FetchContributionsResponse,
  FetchlistContributionsForWorkSplit,
  PatchContributionResponse,
  Contribution,
  ContributionClassesAndRolesDDL,
  ContributionClassName,
} from 'types/contribution';
import {
  getAcceptWorkSplitLink,
  getContributionHref,
  getContributionsForWorkSplitLink,
  getContributionsListLink,
  getContributionUpdateLinkSelector,
  getCreateAudioHref,
  getCreateContributionLinkSelector,
  getCreateFolderLink,
  getCreateSongLinkSelector,
  getCreateSplitLink,
  getDeleteContributionLink,
  getDeleteFolderLink,
  getDeleteSongAudioHref,
  getDownloadAudioHref,
  getDownloadSplitPdfLink,
  getFetchAudioLink,
  getFetchSongAudioTypesLink,
  getFetchSongLinkSelector,
  getFetchSongsWithAudioListsLink,
  getFolder,
  getInviteCollaboratorLink,
  getLanguageCodesDDLinkSelector,
  getLatestSplit,
  getLatestWorkSplitLink,
  getListAudioLink,
  getLocalAudioByGuid,
  getLyricSheetPdfDonwloadLink,
  getLyricsListLink,
  getPutLatestAudioVersionLink,
  getRemoveLatestAudioVersionLink,
  getSetLyricsSheetVersionLink,
  getSongAudioListReadyForUpload,
  getSongCollaborators,
  getSongCountLinkSelector,
  getSongDDLinkSelector,
  getSongGuidSelector,
  getSongListLinkSelector,
  getSongUpdateLinkSelector,
  getUpdateAudioLink,
  getUpdateFolderLink,
} from 'app/store/SongList/selectors';
import {
  buildQueryUrl,
  extractFilenameFromURL,
  triggerDownload,
} from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import { SongFormData } from 'app/components/Song/SongForm/types';
import { SongDetailsFormData } from 'app/components/Song/Details/SongDetailsForm/types';
import {
  Collaborator,
  InviteCollaboratorsPayload,
  InviteCollaboratorResponse,
} from 'types/collaborator.types';
import {
  filterUpgradeMessageLyrics,
  formatEditAudioFormData,
  formatSongPatchData,
  getAddSongsMessage,
  getDeleteAudioErrorMessage,
  getPatchContributionDataForBE,
  getPopulatedLocalAudioInstance,
} from 'app/store/SongList/utils';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { ContributionFormData } from 'app/components/Song/Contributions/ContributionForm/types';
import { isAudioPage } from 'app/views/Song/utils';
import { getContributionClassesRolesDDLLink } from 'app/store/Auth/selectors';
import { getPlayerCurrentAudio } from 'app/store/AudioPlayer/selectors';
import {
  CreateSongResponse,
  DownloadPdfResponse,
  FetchSongCountResponse,
  SongFolderResponse,
  FetchSongItemsResponse,
  FetchSongResponse,
  FetchSongsFoldersResponse,
  Song,
  SongDDL,
  SongListSearchValidationSchemaResponse,
  SongPatchValidationSchemaResponse,
  FetchSongsFolderResponse,
  FetchPinnedSongsResponse,
} from 'types/song';
import { dateFormats } from 'app/constants';
import { SplitShares } from 'types/splits.types';
import { AppState } from 'app/store';
import {
  FetchLyric2SheetResponse,
  FetchSongLyricsListResponse,
  Lyrics2Configuration,
  LyricSheet2,
  LyricsVersion,
} from 'types/lyrics.types';
import { config } from 'config';
import {
  Audio,
  AudioFormValidationSchemaResponse,
  AudioStatusType,
  DownloadAudioResponse,
  FetchAudioResponse,
  FetchSongAudioListResponse,
  FetchSongAudioResponse,
  FetchSongAudioTypesResponse,
  PostAudioResponse,
  SongAudioListSearchValidationSchemaResponse,
  UpdateAudioResponse,
} from 'types/audio';
import { createAsyncThunk } from 'app/store/utils';
import { SearchableProfile } from 'types/notification.types';
import { apiUrls } from 'app/api.config';
import {
  FetchFolderSongsListEffectData,
  FetchSongsListEffectData,
  FetchSongsWithAudioListsData,
  PatchContributionEffectData,
  PostSongAudioEffectData,
  SongDeeplinkEffectData,
  UpdateFolderEffectData,
  UpdateSongAudioEffectData,
} from './effects.types';
import { closePlayerAction } from '../AudioPlayer/slice';
import { addToDownloadList } from '../Shares/slice';
import { AlertIcon } from '../Alerts/slice.types';
import { getProfileSearchLink } from '../Profile/selectors';
import {
  addSongsToFolderAction,
  removeSongsFromFolderAction,
} from '../Mixpanel/slice';
import { getUpdatedFolderItem } from './effects.utils';

export const supportedLyricsVersion = '1.0.0';

export const fetchSongCountEffect = createAsyncThunk(
  'songs/fetchSongCount',
  async (_, { getState, dispatch }) => {
    const link = getSongCountLinkSelector(getState());
    try {
      if (!link) {
        throw new Error('Songs Count Link Not Found');
      }

      const response = await axios.get<FetchSongCountResponse>(link.href, {
        withCredentials: true,
      });

      const { count } = response.data.response;
      return count;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Unexpected error, fetching song count, please try again',
        })
      );
      throw e;
    }
  }
);

export const fetchSongListEffect = createAsyncThunk(
  'songs/fetchList',
  async (data: FetchSongsListEffectData, { getState, dispatch }) => {
    const { next, sortParams, search } = data;
    const link = getSongListLinkSelector(getState());
    try {
      if (!link) {
        throw new Error('Songs Link Not Found');
      }

      dispatch(fetchSongCountEffect());

      const url =
        next?.href ||
        buildQueryUrl(link.href, {
          ...sortParams,
          ...(search ? { search } : {}),
        });

      const response = await axios.get<FetchSongItemsResponse>(url, {
        withCredentials: true,
      });

      const { items, _links } = response.data.response.song;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const createSongEffect = createAsyncThunk<Song, { title?: string }>(
  'songs/create',
  async (data, { getState, rejectWithValue }) => {
    const link = getCreateSongLinkSelector(getState());
    try {
      if (!link) {
        throw new Error('Create Song Link Not Found');
      }
      const defaultTitle = `New Song - ${format(
        new Date(),
        dateFormats.LIST_FORMAT
      )}`;
      const response = await axios.post<CreateSongResponse>(
        link.href,
        {
          title: data.title || defaultTitle,
        },
        {
          withCredentials: true,
        }
      );
      return response.data.response.song.item;
    } catch (e: any) {
      return rejectWithValue(e);
    }
  }
);

export const updateSongEffect = createAsyncThunk(
  'songs/update',
  async (
    data: { formData: Partial<SongFormData & SongDetailsFormData> },
    { getState }
  ) => {
    const updateLink = getSongUpdateLinkSelector(getState());
    if (!updateLink) {
      throw new Error('Update Link Not Found');
    }
    const response = await axios.patch<FetchSongResponse>(
      updateLink.href,
      formatSongPatchData(data.formData),
      {
        withCredentials: true,
      }
    );
    return response.data.response.song.item;
  }
);

export const handleCreateSongWithDeeplinkingEffect = createAsyncThunk<
  {
    shouldRedirectToAuth?: boolean;
    data?: SongDeeplinkEffectData;
    guid?: string;
  },
  SongDeeplinkEffectData
>(
  'songs/handleCreateWithDeeplinking',
  async (data, { getState, dispatch, rejectWithValue }) => {
    const {
      title,
      location,
      creationDate,
      duration,
      tempo,
      keySignature,
      timeSignature,
    } = data.data;
    const state = getState();
    const link = getCreateSongLinkSelector(state);
    try {
      if (!state.auth.authenticated) {
        return rejectWithValue({
          shouldRedirectToAuth: true,
          data: data.data,
          message: 'Unauthorised',
        });
      }
      if (!link) {
        throw new Error('create song link not found');
      }
      const newSong = await dispatch(createSongEffect({ title })).unwrap();

      dispatch(
        addAlertAction({ message: 'Song added, Studio auto-saves your work' })
      );

      if (
        location ||
        creationDate ||
        duration ||
        tempo ||
        keySignature ||
        timeSignature
      ) {
        await dispatch(
          updateSongEffect({
            formData: {
              ...(location ? { location: { address: location } } : {}),
              ...(creationDate ? { creationDate: new Date(creationDate) } : {}),
              ...(duration ? { duration } : {}),
              ...(tempo ? { tempo } : {}),
              ...(keySignature
                ? { keySignature: { value: keySignature, title: 'Signature' } }
                : {}),
              ...(timeSignature
                ? {
                    timeSignature: {
                      value: timeSignature,
                      title: timeSignature,
                    },
                  }
                : {}),
            },
          })
        );
      }

      return { guid: newSong.guid };
    } catch (e: any) {
      if (e?.response?.status === 401) {
        return rejectWithValue({
          shouldRedirectToAuth: true,
          data: data.data,
          message: 'Unauthorised',
        });
      }

      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const fetchSongEffect = createAsyncThunk<
  Song,
  { guid: string; initial?: boolean }
>('songs/fetchSong', async (data, { getState, rejectWithValue }) => {
  const link = getFetchSongLinkSelector(getState(), data.guid);

  try {
    if (!link) {
      throw new Error('Get Song Link Not Found');
    }
    const response = await axios.get<FetchSongResponse>(link, {
      withCredentials: true,
    });
    return response.data.response.song.item;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error);
  }
});

export const fetchUsualSuspectsEffect = createAsyncThunk<SearchableProfile[]>(
  'songs/usualSuspects',
  async (_, { getState, rejectWithValue }) => {
    const link = getProfileSearchLink(getState())?.href;

    try {
      if (!link) {
        throw new Error('Get Usual collaborators Link Not Found');
      }
      const url = buildQueryUrl(link, { limit: '8' });
      const response = await axios.get(url, {
        withCredentials: true,
      });

      return response.data.response?.profile?.items || [];
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.error);
    }
  }
);

export const fetchPinnedSongsEffect = createAsyncThunk(
  'songs/fetchPinnedSongs',
  async (_, { dispatch }) => {
    try {
      const { data } = await axios.get<FetchPinnedSongsResponse>(
        apiUrls.songsPins,
        {
          withCredentials: true,
        }
      );
      return data.response.song;
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const leaveSongEffect = createAsyncThunk(
  'songs/leave',
  async (data: { songGuid: string; href?: Link }, { dispatch, getState }) => {
    const { href, songGuid } = data;
    const currentAudio = getPlayerCurrentAudio(getState());
    try {
      if (!href) {
        throw new Error('Leave Link Not Found');
      }
      await axios.request({
        url: href.templated
          ? href.href.replace('{song.guid}', songGuid)
          : href.href,
        method: href.method,
        withCredentials: true,
      });
      dispatch(fetchPinnedSongsEffect());
      if (currentAudio?.parentGuid === songGuid) {
        dispatch(closePlayerAction());
      }
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const fetchSongValidationSchemaEffect = createAsyncThunk(
  'songs/fetchSongValidationSchemaEffect',
  async (_, { getState }) => {
    const updateLink = getSongUpdateLinkSelector(getState());
    if (!updateLink) {
      throw new Error('Song Update Link Not Found');
    }
    const response = await axios.request<SongPatchValidationSchemaResponse>({
      url: updateLink.href,
      method: 'PATCH',
      headers: { Accept: 'application/json+schema' },
      withCredentials: true,
    });
    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchLanguageCodesDDLEffect = createAsyncThunk(
  'songs/fetchLanguageCodesDDLEffect',
  async (_, { getState }) => {
    const link = getLanguageCodesDDLinkSelector(getState())?.href;
    if (!link) {
      throw new Error('Language Codes DDL Link Not Found');
    }
    const response = await axios.get<FetchDDLResponse>(link);
    return response.data.alpha2;
  }
);

export const fetchSongDDLEffect = createAsyncThunk(
  'songs/fetchSongDDL',
  async (_, { getState }) => {
    const link = getSongDDLinkSelector(getState())?.href;
    if (!link) {
      throw new Error('Song DDL Link Not Found');
    }
    const response = await axios.get<SongDDL>(link);
    return response.data;
  }
);

export const patchContributionEffect = createAsyncThunk(
  'songs/patchContribution',
  async (data: PatchContributionEffectData, { getState, rejectWithValue }) => {
    const { className, contributionGuid, dirtyValues, profileGuid } = data;
    const patchContributionLink = getContributionUpdateLinkSelector(getState());
    if (!patchContributionLink) {
      throw new Error('Contribution Update Link Not Found');
    }
    const patchData = getPatchContributionDataForBE(
      dirtyValues,
      className,
      profileGuid
    );
    if (
      !every(patchData, isEmpty) ||
      some(Object.values(patchData), (value) => value === null)
    ) {
      try {
        const response = await axios.patch<PatchContributionResponse>(
          patchContributionLink.href
            .replace('{className}', className.toLowerCase())
            .replace('{contributionGuid}', contributionGuid),
          patchData,
          {
            withCredentials: true,
          }
        );
        return response.data.response.contribution.item;
      } catch (e: any) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw rejectWithValue(e?.response?.data?.error) || e;
      }
    }
    return null;
  }
);

export const fetchPatchContributionValidationEffect = createAsyncThunk(
  'songs/fetchPatchContributionValidation',
  async ({ className }: { className: ContributionClassName }, { getState }) => {
    const patchContributionLink = getContributionUpdateLinkSelector(getState());
    if (!patchContributionLink) {
      throw new Error('Contribution Update Link Not Found');
    }
    const response = await axios.patch<ContributionPatchValidationSchemaResponse>(
      patchContributionLink.href.replace(
        '{className}',
        className.toLowerCase()
      ),
      {},
      {
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );
    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchCreateContributionValidationsEffect = createAsyncThunk(
  'songs/fetchCreateContributionValidations',
  async (_, { getState }) => {
    const createContributionLink = getCreateContributionLinkSelector(
      getState()
    );

    if (!createContributionLink) {
      throw new Error('Create Contribution Link Not Found');
    }

    const response = await axios.request<ContributionValidationSchemaResponse>({
      url: createContributionLink.href,
      method: createContributionLink.method,
      headers: { Accept: 'application/json+schema' },
      withCredentials: true,
    });
    return response.data.response.schema.properties.body.properties;
  }
);

export const createContributionEffect = createAsyncThunk(
  'songs/createContribution',
  async (data: { formData: Partial<ContributionFormData> }, { getState }) => {
    const { formData } = data;
    const state = getState();
    const createContributionLink = getCreateContributionLinkSelector(state);
    const songGuid = getSongGuidSelector(state);
    try {
      if (!createContributionLink) {
        throw new Error('Create Contribution Link Not Found');
      }
      const response = await axios.post<CreateContributionResponse>(
        createContributionLink.href,
        {
          parent: songGuid,
          name: formData.name,
          className: formData.className,
          profileGuid: formData.profileGuid,
        },
        {
          withCredentials: true,
        }
      );
      return response.data.response.contribution.item;
    } catch (e) {
      throw new Error('Failed to create credit. Please try again');
    }
  }
);

export const fetchContributionsListEffect = createAsyncThunk(
  'songs/fetchContributionsList',
  async (data: { update?: boolean }, { getState, dispatch }) => {
    const link = getContributionsListLink(getState());
    try {
      if (!link) {
        throw new Error('Contributions List Link Not Found');
      }
      const response = await axios.get<FetchContributionsResponse>(link.href, {
        withCredentials: true,
      });
      return response.data.response.contributions;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const deleteSongContributionEffect = createAsyncThunk(
  'songs/deleteContribution',
  async (data: { guid: string }, { getState, dispatch }) => {
    const deleteLink = getDeleteContributionLink(getState());
    try {
      if (!deleteLink) {
        throw new Error('Delete Contribution Link Not Found');
      }
      await axios.request({
        url: deleteLink.href.replace('{contributionGuid}', data.guid),
        method: deleteLink.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Credit deleted' }));
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const deleteSongContributorEffect = createAsyncThunk(
  'songs/deleteContributor',
  async (
    data: { contributions: Partial<Contribution>[] },
    { getState, dispatch }
  ) => {
    const deleteLink = getDeleteContributionLink(getState());
    try {
      if (!deleteLink) {
        throw new Error('Delete Contribution Link Not Found');
      }
      await Promise.all(
        data.contributions.map((contribution) =>
          axios.request({
            url: deleteLink.href.replace(
              '{contributionGuid}',
              contribution.guid || ''
            ),
            method: deleteLink.method,
            withCredentials: true,
          })
        )
      );
      const deletedContributionGuids = data.contributions.map(
        (contribution) => contribution.guid
      );
      dispatch(addAlertAction({ message: 'Contributor removed' }));
      return { guids: deletedContributionGuids };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const fetchContributionEffect = createAsyncThunk(
  'songs/fetchContribution',
  async (data: { guid: string }, { getState, dispatch }) => {
    const fetchContributionHref = getContributionHref(getState());
    try {
      if (!fetchContributionHref) {
        throw new Error('Fetch Contribution Link Not Found');
      }
      const response = await axios.get<FetchContributionResponse>(
        fetchContributionHref.replace('{contributionGuid}', data.guid),
        {
          withCredentials: true,
        }
      );
      return response.data.response.contribution.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to fetch contribution, Please try again',
        })
      );
      return null;
    }
  }
);

export const postSongAudioEffect = createAsyncThunk<
  Audio,
  { audio: PostSongAudioEffectData; songGuid?: string },
  { rejectValue: Audio; state: AppState }
>(
  'songs/postAudio',
  async ({ audio, songGuid }, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const { file, fileMeta, retryAudio } = audio;
    const createAudioHref = songGuid
      ? apiUrls.uploadAudio.replace(':songGuid', songGuid)
      : getCreateAudioHref(state);

    try {
      if (!createAudioHref) {
        throw new Error('Upload Audio Link Not Found');
      }
      const response = await axios.post<PostAudioResponse>(
        createAudioHref,
        { ...fileMeta, originalFileName: file.name, fileSize: file.size },
        { withCredentials: true }
      );
      return response.data.response.audio.item;
    } catch (e: any) {
      const isMinimumSizeError = e.response?.data?.error?.details?.find(
        (el: any) => el?.errorType === 'minimum'
      );
      const isMaximumsSizeError =
        e.response?.data?.error?.errorCode ===
          ApiErrorCodeTypes.INSUFFICIENT_STORAGE ||
        e.response?.data?.error?.details?.find(
          (el: any) => el?.errorType === 'maximum'
        );

      let message = `Failed to upload "${file.name}" audio. Please try again`;

      if (isMinimumSizeError) {
        message = `Audio file "${file.name}" is too small. Please try again with a larger file`;
      }

      if (isMaximumsSizeError) {
        message = `Audio file "${file.name}" is too large. Please try again with a smaller file`;
      }

      dispatch(addAlertAction({ message }));

      if (!retryAudio && !isMinimumSizeError && !isMaximumsSizeError) {
        const populatedLocalAudio = getPopulatedLocalAudioInstance(
          { ...fileMeta, file } as Audio,
          state
        ) as Audio;
        return rejectWithValue(populatedLocalAudio);
      }
      throw e;
    }
  }
);

export const uploadAudioEffect = createAsyncThunk(
  'songs/uploadAudio',
  async (data: { audioFile: File; audio: Audio }, { getState, dispatch }) => {
    const { audioFile, audio } = data;
    try {
      await axios.put<PostAudioResponse>(audio._links.upload.href, audioFile, {
        headers: { 'Content-Type': audioFile.type },
      });
    } catch (e) {
      const state = getState();
      const doesLocalAudioExist = getLocalAudioByGuid(
        state,
        audio.guid,
        audio.parent
      );
      if (doesLocalAudioExist) {
        const pathname = config.isElectronBuild
          ? window.location.hash?.substring(1)
          : window.location.pathname;
        const songGuid = getSongGuidSelector(state);

        if (isAudioPage(pathname, songGuid)) {
          dispatch(
            addAlertAction({
              message: 'Failed to upload audio. Please try again',
            })
          );
        }
      }
      throw e;
    }
  }
);

export const retryAudioEffect = createAsyncThunk(
  'songs/retryAudio',
  async (data: { audio: Audio; songGuid?: string }, { dispatch }) => {
    const { audio, songGuid } = data;
    try {
      if (!audio.file) {
        throw new Error('Failed to retrieve audio file');
      }
      const createdAudio = await dispatch(
        postSongAudioEffect({
          audio: {
            fileMeta: {
              type: audio.type,
              remark: audio.remark,
              title: audio.title,
            },
            file: audio.file,
            retryAudio: audio,
          },
          songGuid,
        })
      ).unwrap();
      await dispatch(
        uploadAudioEffect({
          audioFile: audio.file,
          audio: createdAudio,
        })
      );
    } catch (e) {
      // proceed
    }
  }
);

export const fetchSongAudioTypesEffect = createAsyncThunk(
  'songs/fetchAudioTypes',
  async (_, { getState }) => {
    const link = getFetchSongAudioTypesLink(getState());
    if (!link) {
      throw new Error('Fetch Audio Types DDL link not found');
    }
    const response = await axios.get<FetchSongAudioTypesResponse>(link?.href, {
      withCredentials: false,
    });
    return response.data.types;
  }
);

export const fetchSongAudioValidationsEffect = createAsyncThunk(
  'songs/fetchAudioValidations',
  async (_, { getState }) => {
    const createAudioHref = getCreateAudioHref(getState());
    if (!createAudioHref) {
      throw new Error('Create Audio link not found');
    }
    const response = await axios.post<AudioFormValidationSchemaResponse>(
      createAudioHref,
      undefined,
      {
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );
    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchSongAudioListEffect = createAsyncThunk<Audio[]>(
  'songs/fetchAudioList',
  async (_, { getState }) => {
    const audioListLink = getListAudioLink(getState());
    if (!audioListLink) {
      throw new Error('Fetch Song Audio List link not found');
    }
    const readyForUploadAudioList = getSongAudioListReadyForUpload(getState());
    const url = buildQueryUrl(audioListLink.href, {
      includeReadyForUpload: readyForUploadAudioList.length ? 'true' : '',
    });
    const response = await axios.get<FetchSongAudioListResponse>(url, {
      withCredentials: true,
    });
    const { items } = response.data.response.audio;

    if (readyForUploadAudioList.length) {
      return items.filter(
        (audio) =>
          !(
            audio.status === AudioStatusType.READY_FOR_UPLOAD &&
            !readyForUploadAudioList.includes(audio.guid)
          )
      );
    }

    return items;
  }
);

export const downloadAudioEffect = createAsyncThunk(
  'songs/downloadAudio',
  async (data: { guid: string }, { getState, dispatch }) => {
    const downloadAudioHref = getDownloadAudioHref(getState());
    try {
      if (!downloadAudioHref) {
        throw new Error('Download Audio link not found');
      }
      const response = await axios.get<DownloadAudioResponse>(
        downloadAudioHref.replace('{audioGuid}', data.guid),
        {
          withCredentials: true,
        }
      );

      const { download } = response.data.response._links;
      const fileName = extractFilenameFromURL(decodeURI(download.href));
      triggerDownload(download.href, fileName);
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to download file. Please try again' })
      );
      throw e;
    }
  }
);

export const deleteSongAudioEffect = createAsyncThunk(
  'songs/deleteAudio',
  async (data: { audio: Audio }, { getState, dispatch }) => {
    const state = getState();
    const { audio: audioToDelete } = data;
    const currentAudio = getPlayerCurrentAudio(state);
    const deleteAudiohref = getDeleteSongAudioHref(state);
    try {
      if (!deleteAudiohref) {
        throw new Error('Delete Song Audio link not found');
      }
      await axios.delete<FetchSongAudioResponse>(
        deleteAudiohref.replace('{audioGuid}', audioToDelete.guid),
        {
          withCredentials: true,
        }
      );
      if (currentAudio?.audioGuid === audioToDelete.guid) {
        dispatch(closePlayerAction());
      }
      dispatch(addAlertAction({ message: `"${audioToDelete.title}" deleted` }));
    } catch (e) {
      dispatch(
        addAlertAction({
          message: getDeleteAudioErrorMessage(e),
        })
      );
      throw e;
    }
  }
);

export const updateSongAudioEffect = createAsyncThunk(
  'songs/updateAudio',
  async (data: UpdateSongAudioEffectData, { getState, dispatch }) => {
    const { audio, formData } = data;
    const updateAudioLink = getUpdateAudioLink(getState());
    try {
      if (!updateAudioLink) {
        throw new Error('Update Song Audio link not found');
      }
      const response = await axios.patch<UpdateAudioResponse>(
        updateAudioLink?.href.replace('{audioGuid}', audio.guid),
        formatEditAudioFormData(formData),
        {
          withCredentials: true,
        }
      );
      const { item } = response.data.response.audio;
      return {
        ...item,
        remark: item.remark || formData.remark || '',
      };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to update audio. Please try again' })
      );
      throw e;
    }
  }
);

export const fetchSongAudioEffect = createAsyncThunk(
  'songs/fetchAudio',
  async (data: { guid: string }, { getState }) => {
    const state = getState();
    const fetchAudioLink = getFetchAudioLink(state);
    if (!fetchAudioLink) {
      throw new Error('Fetch Song Audio link not found');
    }
    const response = await axios.get<FetchAudioResponse>(
      fetchAudioLink?.href.replace('{audioGuid}', data.guid),
      {
        withCredentials: true,
      }
    );
    return response.data.response.audio.item;
  }
);

export const inviteCollaboratorsEffect = createAsyncThunk(
  'songs/inviteCollaborators',
  async (
    data: {
      payload: InviteCollaboratorsPayload;
    },
    { getState, dispatch }
  ) => {
    const state = getState();
    const inviteCollaboratorsLink = getInviteCollaboratorLink(state);
    const collaborators = getSongCollaborators(state);
    const songGuid = getSongGuidSelector(getState());
    const fetchSongLink = getFetchSongLinkSelector(getState(), songGuid || '');
    const { payload } = data;
    try {
      if (!inviteCollaboratorsLink || !fetchSongLink) {
        throw new Error(
          'Fetch Invite collaborators / Song Fetch link not found'
        );
      }
      await axios.post<InviteCollaboratorResponse>(
        inviteCollaboratorsLink?.href,
        {
          ...payload,
          inviteeUserGuids: payload.inviteeUserGuids?.map(({ guid }) => guid),
        },
        {
          withCredentials: true,
        }
      );
      const songResponse = await axios.get<FetchSongResponse>(fetchSongLink, {
        withCredentials: true,
      });

      // TODO: Need approved generic message for invited collaborators
      dispatch(
        addAlertAction({
          message: `Invite sent to collaborators`,
        })
      );
      return songResponse.data?.response?.song.item.collaborators || [];
    } catch (e: any) {
      if (
        e.response?.data?.error?.errorCode ===
        ApiErrorCodeTypes.MAX_COLLABORATOR_COUNT_EXCEEDED
      ) {
        const count = collaborators?.length || '';
        const message = ApiErrorCodeMessages.MAX_COLLABORATOR_COUNT_EXCEEDED.replace(
          '{NUM}',
          count ? `(${count})` : ''
        );
        dispatch(addAlertAction({ message }));
      } else if (
        e.response?.data?.error?.statusReason === ApiErrorCodeTypes.CONFLICT
      ) {
        e.response?.data?.error?.details?.forEach(
          ({ message, guid }: { message: string; guid?: string }) => {
            let alertMessage = message;

            if (guid) {
              const displayName = collaborators?.find(
                ({ userGuid }) => userGuid === guid
              )?.profileCard?.displayName;

              if (displayName) {
                alertMessage = alertMessage.replace(guid, displayName);
              }
            }
            dispatch(
              addAlertAction({
                message: alertMessage,
              })
            );
          }
        );
      } else {
        dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      }
      throw e;
    }
  }
);

export const getLyrics2ConnectionEffect = createAsyncThunk<
  {
    lyricSheet: LyricSheet2;
    connect: LyricSheet2['_links']['connect'];
    configuration: Lyrics2Configuration;
  },
  { songGuid: string; songName: string }
>(
  'songs/getLyrics2Connection',
  async (data, { getState, dispatch, rejectWithValue }) => {
    try {
      const lyricsListLink = getLyricsListLink(getState());

      if (!lyricsListLink) {
        throw new Error('lyric list link not found');
      }
      const {
        data: {
          response: {
            lyricSheet: { _links, items },
          },
        },
      } = await axios.request<FetchSongLyricsListResponse>({
        url: lyricsListLink.href,
        method: lyricsListLink.method,
        withCredentials: true,
      });

      const lyricListItem = filterUpgradeMessageLyrics(items)[0];

      if (lyricListItem) {
        if (!_links.getLyricSheet) {
          throw new Error('get lyric link not found');
        }

        const url = buildQueryUrl(
          _links.getLyricSheet.href
            .replace('{lyricSheet.parent}', lyricListItem.parent)
            .replace('{lyricSheet.guid}', lyricListItem.guid),
          { supportedVersion: supportedLyricsVersion }
        );
        const response = await axios.request<FetchLyric2SheetResponse>({
          url,
          method: _links.getLyricSheet.method,
          withCredentials: true,
        });

        const { lyricSheet, configuration } = response.data.response;
        return {
          lyricSheet: lyricSheet.item,
          connect: lyricSheet.item._links.connect,
          configuration: configuration.item,
        };
      }

      if (!_links.createLyricSheet) {
        throw new Error('create lyric link not found');
      }
      const response = await axios.request<FetchLyric2SheetResponse>({
        url: _links.createLyricSheet.href.replace(
          '{lyricSheet.parent}',
          data.songGuid
        ),
        data: {
          title: data.songName,
          supportedVersion: supportedLyricsVersion,
        },
        method: _links.createLyricSheet.method,
        withCredentials: true,
      });

      const { lyricSheet, configuration } = response.data.response;

      return {
        lyricSheet: lyricSheet.item,
        connect: lyricSheet.item._links.connect,
        configuration: configuration.item,
      };
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode !==
        ApiErrorCodeTypes.CONFIGURATION_VERSION_NOT_SUPPORTED
      ) {
        dispatch(
          addAlertAction({ message: 'Failed to load lyrics. Please try again' })
        );
      }
      return rejectWithValue(e);
    }
  }
);

export const removeSongCollaboratorEffect = createAsyncThunk(
  'songs/removeSongCollaborator',
  async (data: { collaborator: Collaborator }, { dispatch }) => {
    const { collaborator } = data;
    const removeCollaboratorLink = collaborator._links?.deleteCollaborator;
    try {
      if (!removeCollaboratorLink) {
        throw new Error('Delete Collaborator link not found');
      }

      await axios.request({
        url: removeCollaboratorLink.href,
        method: removeCollaboratorLink.method,
        withCredentials: true,
      });

      dispatch(
        addAlertAction({
          message: `${
            collaborator.profile?.displayName || collaborator.inviteeEmail
          } removed as a collaborator`,
        })
      );
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to remove collaborator. Please try again',
        })
      );
    }
  }
);

export const fetchContributionClassesRolesDDLEffect = createAsyncThunk(
  'songs/fetchContributionClassesRolesDDL',
  async (_, { getState }) => {
    const link = getContributionClassesRolesDDLLink(getState());
    if (!link) {
      throw new Error('Contribution classes and roles DDL link not found');
    }

    const response = await axios.request<ContributionClassesAndRolesDDL>({
      url: link.href,
      method: link.method,
    });
    return response.data;
  }
);

export const setSongLatestAudioVersionEffect = createAsyncThunk(
  'songs/setSongLatestAudioVersion',
  async (
    data: {
      audioGuid: string;
    },
    { dispatch, getState }
  ) => {
    const link = getPutLatestAudioVersionLink(getState());
    try {
      if (!link) {
        throw new Error('Put latest audio version link not found');
      }

      await axios.request<ContributionClassesAndRolesDDL>({
        url: link.href,
        method: link.method,
        data: {
          latestVersionAudio: data.audioGuid,
        },
        withCredentials: true,
      });
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to update latest audio version. Please try again',
        })
      );
    }
  }
);

export const removeSongLatestAudioVersionEffect = createAsyncThunk(
  'songs/removeSongLatestAudioVersion',
  async (
    data: {
      audioGuid: string;
    },
    { dispatch, getState }
  ) => {
    const link = getRemoveLatestAudioVersionLink(getState());
    try {
      if (!link) {
        throw new Error('Remove latest audio version link not found');
      }
      await axios.request<ContributionClassesAndRolesDDL>({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to remove latest audio version. Please try again',
        })
      );
    }
  }
);

export const fetchPossibleSplitMembersEffect = createAsyncThunk(
  'songs/fetchPossibleSplitMembers',
  async (_, { dispatch, getState }) => {
    const link = getContributionsForWorkSplitLink(getState());
    try {
      if (!link) {
        throw new Error('Contributions list link not found');
      }
      const response = await axios.get<FetchlistContributionsForWorkSplit>(
        link.href,
        {
          withCredentials: true,
        }
      );
      return response.data.response.contributions.items;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to fetch members list. Please try again',
        })
      );
      throw e;
    }
  }
);

export const createSplitEffect = createAsyncThunk(
  'songs/createSplit',
  async (data: { splitShares: SplitShares }, { dispatch, getState }) => {
    const link = getCreateSplitLink(getState());
    const songGuid = getSongGuidSelector(getState());
    const previousSplit = getLatestSplit(getState());
    try {
      if (!link) {
        throw new Error('Create split link not found');
      }
      const parsedShares = data.splitShares.members.map((member) => ({
        share: Math.round(+member.share * 100),
        memberIdentifier: member.memberIdentifier,
      }));

      const response = await axios.request<FetchLatestSplitResponse>({
        url: link.href,
        method: link.method,
        data: {
          members: parsedShares,
          songGuid,
          type: 'work',
          previousSequenceNumber: previousSplit?.sequenceNumber,
        },
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Split created successfully' }));
      return response.data.response.split.item;
    } catch (e: any) {
      const { error = {} } = e.response?.data;
      if (error.statusReason === ApiErrorCodeTypes.CONFLICT) {
        dispatch(
          addAlertAction({
            message:
              'There is a more recent version of this split. Please refresh',
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: 'Failed to create split. Please try again',
          })
        );
      }
      throw e;
    }
  }
);

export const fetchLatestSplitEffect = createAsyncThunk(
  'songs/fetchLatestSplit',
  async (_, { dispatch, getState }) => {
    const link = getLatestWorkSplitLink(getState());
    try {
      if (!link) {
        throw new Error('Get Latest Work Split link not found');
      }
      const response = await axios.get<FetchLatestSplitResponse>(link.href, {
        withCredentials: true,
      });
      return response.data.response.split.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to get the split. Please try again' })
      );
      throw e;
    }
  }
);

export const acceptSplitEffect = createAsyncThunk(
  'songs/acceptSplit',
  async (_, { dispatch, getState }) => {
    const link = getAcceptWorkSplitLink(getState());
    try {
      if (!link) {
        throw new Error('Create split link not found');
      }
      const response = await axios.request<FetchLatestSplitResponse>({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
      return response.data.response.split.item;
    } catch (e: any) {
      const { error = {} } = e.response?.data;
      if (error.statusReason === ApiErrorCodeTypes.CONFLICT) {
        dispatch(
          addAlertAction({
            message:
              'There is a more recent version of this split. Please refresh',
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: 'Failed to accept the split. Please try again',
          })
        );
      }
      throw e;
    }
  }
);

export const fetchSongsWithAudioListsEffect = createAsyncThunk(
  'songs/fetchSongsWithAudioLists',
  async (data: FetchSongsWithAudioListsData, { getState, dispatch }) => {
    const {
      next,
      search,
      types = [],
      validationContext,
      onlyLatestVersionAudio,
    } = data;
    const link = getFetchSongsWithAudioListsLink(getState());
    try {
      if (!link) {
        throw new Error('Fetch Songs With Audio Lists Link Not Found');
      }
      const url = buildQueryUrl(next?.href || link.href, {
        ...(search ? { search } : {}),
        types,
        onlyLatestVersionAudio: String(onlyLatestVersionAudio),
        ...(validationContext ? { validationContext } : {}),
      });
      const response = await axios.get<FetchSongItemsResponse>(url, {
        withCredentials: true,
      });
      const { items, _links } = response.data.response.song;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const downloadLyricsPdfEffect = createAsyncThunk(
  'songs/download-lyrics-pdf',
  async (data: { lyricGuid?: string }, { getState, dispatch }) => {
    const link = getLyricSheetPdfDonwloadLink(getState());
    try {
      if (!link) {
        throw new Error('Get Lyrics Sheet Pdf link not found');
      }
      const response = await axios.get<DownloadPdfResponse>(link.href, {
        withCredentials: true,
      });
      const downloadUrl = response.data.response?._links?.getFile?.href?.url;
      if (downloadUrl) {
        triggerDownload(downloadUrl);
      } else if (response.status === 202 && data.lyricGuid) {
        dispatch(addToDownloadList(data.lyricGuid));
        dispatch(
          addAlertAction({
            id: data.lyricGuid,
            message: 'Preparing download',
            statusIcon: AlertIcon.LOADING,
            autoHideDuration: 15000,
          })
        );
      }
    } catch (e: any) {
      dispatch(
        addAlertAction({
          message: 'Failed to download Lyrics Sheet PDF, please try again',
        })
      );
      throw e;
    }
  }
);

export const downloadSplitPdfEffect = createAsyncThunk(
  'songs/download-split-pdf',
  async (data: { songGuid?: string }, { getState, dispatch }) => {
    const link = getDownloadSplitPdfLink(getState());
    try {
      if (!link) {
        throw new Error('Get Split Pdf link not found');
      }
      const response = await axios.get<DownloadPdfResponse>(link.href, {
        withCredentials: true,
      });
      const donwloadUrl = response.data.response?._links?.getFile?.href?.url;
      if (donwloadUrl) {
        triggerDownload(donwloadUrl);
      } else if (response.status === 202 && data.songGuid) {
        dispatch(addToDownloadList(data.songGuid));
        dispatch(
          addAlertAction({
            id: data.songGuid,
            message: 'Preparing download',
            statusIcon: AlertIcon.LOADING,
            autoHideDuration: 15000,
          })
        );
      }
    } catch (e: any) {
      dispatch(
        addAlertAction({
          message: 'Failed to download Split PDF, please try again',
        })
      );
      throw e;
    }
  }
);

export const setLyricsSheetVersionEffect = createAsyncThunk(
  'songs/setLyricsSheetVersion',
  async (data: { version: LyricsVersion }, { getState, dispatch }) => {
    const state = getState();
    const setVersionLink = getSetLyricsSheetVersionLink(state);
    try {
      if (!setVersionLink) {
        throw new Error('Set lyric sheet version link not found');
      }
      const response = await axios.request<FetchSongResponse>({
        url: setVersionLink.href,
        method: setVersionLink.method,
        data,
        withCredentials: true,
      });
      return {
        _links: response.data.response.song.item._links,
        lyricsVersion: { version: data.version },
      };
    } catch (e: any) {
      const isConflict =
        e.response?.data?.error?.errorCode ===
        ApiErrorCodeTypes.LYRIC_VERSION_ALREADY_SET;

      dispatch(
        addAlertAction({
          message: isConflict
            ? ApiErrorCodeMessages.LYRIC_VERSION_ALREADY_SET
            : 'Unexpected error, please try again',
        })
      );
      throw e;
    }
  }
);

export const fetchSongListSearchValidationSchema = createAsyncThunk(
  'songs/fetchSongListSearchValidationSchema',
  async (_, { getState }) => {
    const getLink = getSongListLinkSelector(getState());
    if (!getLink) {
      throw new Error('Song List Link Not Found');
    }
    const response = await axios.request<SongListSearchValidationSchemaResponse>(
      {
        url: buildQueryUrl(getLink.href, {}),
        method: 'GET',
        headers: { Accept: 'application/json+schema' },
        withCredentials: true,
      }
    );
    return response.data.response.schema.properties.query.properties;
  }
);

export const fetchSongsWithAudioListSearchValidationSchema = createAsyncThunk(
  'songs/fetchSongsWithAudioListSearchValidationSchema',
  async (_, { getState }) => {
    const getLink = getFetchSongsWithAudioListsLink(getState());
    if (!getLink) {
      throw new Error('Song List with Audio Link Not Found');
    }
    const response = await axios.request<SongAudioListSearchValidationSchemaResponse>(
      {
        url: buildQueryUrl(getLink.href, {}),
        method: 'GET',
        headers: { Accept: 'application/json+schema' },
        withCredentials: true,
      }
    );
    return response.data.response.schema.properties.query.properties;
  }
);

export const resendCollaboratorInviteEffect = createAsyncThunk(
  'songs/resendCollaboratorInvite',
  async (collaborator: Collaborator, { dispatch }) => {
    const resendCollaboratorInviteLink = collaborator._links?.resendInvite;
    try {
      if (!resendCollaboratorInviteLink) {
        throw new Error('Fetch Resend collaborator invite link not found');
      }

      await axios.request({
        url: resendCollaboratorInviteLink.href,
        method: resendCollaboratorInviteLink.method,
        withCredentials: true,
      });

      const resentTo =
        collaborator.inviteeEmail ||
        collaborator?.profile?.displayName ||
        'collaborator';

      dispatch(
        addAlertAction({
          message: `Invite to ${resentTo} resent`,
        })
      );

      return collaborator;
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const fetchFoldersEffect = createAsyncThunk(
  'songs/fetchFolders',
  async (next?: Link) => {
    const link = next?.href || `${apiUrls.songsFolders}`;
    const response = await axios.get<FetchSongsFoldersResponse>(link, {
      withCredentials: true,
    });
    return response.data.response.folder;
  }
);

export const fetchFolderEffect = createAsyncThunk(
  'songs/fetchFolder',
  async (data: FetchFolderSongsListEffectData, { dispatch }) => {
    const { guid, next, sortParams, search } = data;
    const href = `${apiUrls.songsFolders}/${guid}`;
    try {
      dispatch(fetchSongCountEffect());

      const url =
        next?.href ||
        buildQueryUrl(href, {
          ...sortParams,
          ...(search ? { search } : {}),
        });

      const response = await axios.get<FetchSongsFolderResponse>(url, {
        withCredentials: true,
      });

      return response.data.response;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const createFolderEffect = createAsyncThunk(
  'songs/createFolder',
  async (body: { title: string }, { getState, dispatch }) => {
    const link = getCreateFolderLink(getState());
    try {
      if (!link) {
        throw new Error('Create Folder Link Not Found');
      }

      const response = await axios.request<SongFolderResponse>({
        url: link.href,
        method: link.method,
        data: body,
        withCredentials: true,
      });
      return response.data.response.folder.item;
    } catch (e: any) {
      if (e.response.status === 409) {
        dispatch(addAlertAction({ message: 'Song folder already exists' }));
      } else {
        dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      }
      throw e;
    }
  }
);

export const updateFolderEffect = createAsyncThunk(
  'songs/updateFolder',
  async ({ guid, ...body }: UpdateFolderEffectData, { getState, dispatch }) => {
    const { title, addSongs, removeSongs } = body;
    if (!title && !addSongs && !removeSongs) {
      throw new Error('Nothing to update');
    }

    if ([title, addSongs, removeSongs].filter((v) => v).length > 1) {
      throw new Error("Can't do multiple updates at once");
    }

    const link = getUpdateFolderLink(guid)(getState());
    try {
      if (!link) {
        throw new Error('Update Folder Link Not Found');
      }

      const item = await getUpdatedFolderItem({
        link,
        data: body,
      });

      if (addSongs) {
        const folder = getFolder(guid)(getState());
        const songsAdded = (item?.songCount || 0) - (folder?.songCount || 0);

        if (songsAdded > 0) {
          dispatch(
            addSongsToFolderAction({
              folderId: item.guid,
              songsAdded,
            })
          );
        }

        dispatch(
          addAlertAction({
            message: getAddSongsMessage(
              item.title,
              addSongs.length,
              songsAdded
            ),
          })
        );
      }

      if (removeSongs) {
        dispatch(
          removeSongsFromFolderAction({
            folderId: guid,
            songsRemoved: removeSongs.length,
          })
        );
      }

      return item;
    } catch (e: any) {
      if (e.response.status === 409) {
        dispatch(
          addAlertAction({
            message: 'Song folder with this name already exists',
          })
        );
      } else {
        dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      }
      throw e;
    }
  }
);

export const orderFolderEffect = createAsyncThunk(
  'songs/orderFolder',
  async (
    data: { folderGuid: string; targetFolderGuid: string; before: boolean },
    { getState, dispatch }
  ) => {
    const link = getUpdateFolderLink(data.folderGuid)(getState());
    try {
      if (!link) {
        throw new Error('Move Folder Link Not Found');
      }

      await axios.request<SongFolderResponse>({
        url: link.href,
        method: link.method,
        data: {
          targetFolderGuid: data.targetFolderGuid,
          before: data.before,
        },
        withCredentials: true,
      });
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Failed to move Song folder' }));
    }
  }
);

export const deleteFolderEffect = createAsyncThunk(
  'songs/deleteFolder',
  async ({ guid }: { guid: string }, { getState, dispatch }) => {
    const link = getDeleteFolderLink(guid)(getState());
    try {
      if (!link) {
        throw new Error('Delete Folder Link Not Found');
      }

      await axios.request({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
      return getFolder(guid)(getState());
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const pinSongEffect = createAsyncThunk(
  'songs/pinSong',
  async ({ guid, title }: { guid: string; title: string }, { dispatch }) => {
    try {
      await axios.post(
        apiUrls.songsPins,
        {
          songGuid: guid,
        },
        {
          withCredentials: true,
        }
      );
      dispatch(fetchPinnedSongsEffect());
      dispatch(
        addAlertAction({ message: `Song ${title} was pinned successfully` })
      );
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const unpinSongEffect = createAsyncThunk(
  'songs/unpinSong',
  async ({ guid, title }: { guid: string; title: string }, { dispatch }) => {
    try {
      await axios.delete(`${apiUrls.songsPins}/${guid}`, {
        withCredentials: true,
      });
      dispatch(fetchPinnedSongsEffect());
      dispatch(
        addAlertAction({ message: `Song ${title} was unpinned successfully` })
      );
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const reorderPinnedSongsEffect = createAsyncThunk(
  'songs/reorderPinnedSongs',
  async (
    {
      songGuid,
      targetSongGuid,
      before,
    }: { songGuid: string; targetSongGuid: string; before: boolean },
    { dispatch }
  ) => {
    try {
      await axios.patch(
        `${apiUrls.songsPins}/${songGuid}`,
        {
          targetSongGuid,
          before,
        },
        {
          withCredentials: true,
        }
      );
      dispatch(fetchPinnedSongsEffect());
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);
